import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Play, Clock } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export default function ContentCard({ item, to, onClick, index = 0, vertical = false, showPlay = false }) {
  const { t } = useLang();
  const cover = mediaUrl(item.cover_path);
  const aspect = vertical ? "aspect-[9/16]" : "aspect-[4/3]";

  const inner = (
    <>
      <div className={`${aspect} bg-[#F3E8E2] overflow-hidden relative`}>
        {cover ? (
          <img src={cover} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="font-serif text-5xl text-[rgba(198,137,158,0.2)]">{item.title?.charAt(0) || "U"}</span>
          </div>
        )}
        {showPlay && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-[rgba(13,12,11,0.55)] backdrop-blur-sm border border-[rgba(61,50,56,0.3)] flex items-center justify-center group-hover:bg-[#C6899E] transition-colors">
              <Play className="w-5 h-5 text-white group-hover:text-[#4A2233] ml-0.5 transition-colors" />
            </div>
          </div>
        )}
        {item.is_premium && (
          <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-[rgba(13,12,11,0.7)] backdrop-blur-sm border border-[rgba(198,137,158,0.4)] rounded-full px-2.5 py-1">
            <Lock className="w-3 h-3 text-[#C6899E]" />
            <span className="text-[0.62rem] uppercase tracking-[0.12em] text-[#C6899E]">Premium</span>
          </div>
        )}
      </div>
      <div className="pt-4 pb-2">
        <h3 className="font-serif text-xl text-[#3D3238] group-hover:text-[#C6899E] transition-colors leading-snug">{item.title}</h3>
        {item.excerpt && <p className="mt-2 text-sm text-[#8C7E83] line-clamp-2 leading-relaxed">{item.excerpt}</p>}
        <div className="mt-3 flex items-center gap-3 text-xs text-[#9C8F94]">
          {item.reading_time ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {item.reading_time} {t("reading_time")}</span> : null}
          {item.tags?.slice(0, 2).map((tg) => <span key={tg} className="uppercase tracking-[0.1em]">#{tg}</span>)}
        </div>
      </div>
    </>
  );

  const cls = "hover-lift group block text-left w-full";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.06 }}
    >
      {to ? (
        <Link to={to} className={cls} data-testid={`content-card-${item.id}`}>{inner}</Link>
      ) : (
        <button onClick={onClick} className={cls} data-testid={`content-card-${item.id}`}>{inner}</button>
      )}
    </motion.div>
  );
}
